// Requires component to be using react router higher order component so route props are available
import { createSelector } from 'reselect';
import { generatePath, matchPath, RouteProps } from 'react-router';

import { getIsGiftAidable, getTeamGuid } from './apiSelectors';
import { ROUTES } from '../consts';
import { getLocks } from './locksSelectors';

export type JoinRoutes = {
  currentIndex: number;
  steps: (string | undefined)[];
  nextStep?: string;
  prevStep?: string;
  redirect: string | boolean;
};

export const getRouteLocation = (_state: unknown, props: RouteProps) =>
  (props.location && props.location.pathname) || '';

export const getJoinRoutes = createSelector(
  [getTeamGuid, getLocks, getRouteLocation, getIsGiftAidable],
  (teamGuid, locks, location, giftAidable) => {
    if (!teamGuid) {
      throw new Error('Join route cannot be created without a teamGuid');
    }

    const joinPage = generatePath(ROUTES.JOIN, { teamGuid });

    const eventsPage = !locks.event
      ? generatePath(ROUTES.EVENTS, { teamGuid })
      : undefined;

    const causePage = !locks.charity
      ? generatePath(ROUTES.CAUSE, { teamGuid })
      : undefined;

    const frpSettingsPage = generatePath(ROUTES.FUNDRAISING_SETTINGS, {
      teamGuid,
    });

    const giftaidPage = giftAidable
      ? generatePath(ROUTES.GIFT_AID, { teamGuid })
      : undefined;

    const joinSteps = [
      causePage,
      eventsPage,
      frpSettingsPage,
      giftaidPage,
    ].filter(step => step); // Filter out undefined pages

    const currentIndex = joinSteps.indexOf(location);

    const currentlyOnStartPage = matchPath(location, {
      path: ROUTES.JOIN,
      exact: true,
      strict: false,
    });

    return {
      currentIndex,
      steps: joinSteps,
      nextStep: joinSteps[currentIndex + 1],
      prevStep: joinSteps[currentIndex - 1],
      redirect: currentIndex === -1 && !currentlyOnStartPage && joinPage,
    };
  },
);
